import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { FileText, Loader2, Star, X, ImagePlus, ImageOff, Upload } from "lucide-react";

const MAX = 20 * 1024 * 1024;
const GALLERY_MAX = 12;

async function uploadFile(file, kind) {
  if (file.size > MAX) throw new Error("Fichier trop volumineux (20 Mo max)");
  const fd = new FormData();
  fd.append("file", file);
  fd.append("kind", kind);
  const { data } = await api.post("/files", fd);
  return data;
}

const errMsg = (e) => apiError(e.response?.data?.detail) || e.message || "Envoi impossible";

export function useStoredUrl(value) {
  const [url, setUrl] = useState(value && !String(value).startsWith("file:") ? value : "");
  const [type, setType] = useState(String(value || "").startsWith("data:application/pdf") ? "application/pdf" : "");
  useEffect(() => {
    if (!value || !String(value).startsWith("file:")) { setUrl(value || ""); return undefined; }
    let obj = "";
    api.get(`/files/${value.slice(5)}`, { responseType: "blob" })
      .then(({ data }) => { obj = URL.createObjectURL(data); setUrl(obj); setType(data.type); })
      .catch(() => { setUrl(""); setType("error"); });
    return () => { if (obj) URL.revokeObjectURL(obj); };
  }, [value]);
  return { url, type };
}

export function StoredImg({ value, ...props }) {
  const { url, type } = useStoredUrl(value);
  if (url) return <img src={url} alt="" {...props} />;
  return <span className={`flex items-center justify-center bg-zinc-800 text-zinc-600 ${type === "error" ? "" : "animate-pulse"} ${props.className || ""}`}>{type === "error" && <ImageOff className="h-4 w-4" />}</span>;
}

function GalleryThumb({ value, isMain, onMain, onRemove, testid }) {
  return (
    <div className={`group relative h-20 w-20 overflow-hidden rounded-lg border ${isMain ? "border-[#FF6A00]" : "border-zinc-800"}`} data-testid={testid}>
      <StoredImg value={value} className="h-full w-full object-cover" />
      {isMain && <span className="absolute left-1 top-1 rounded-full bg-[#FF6A00] px-1.5 text-[10px] font-semibold text-white" data-testid={`${testid}-main-badge`}>Principale</span>}
      <div className="absolute inset-x-0 bottom-0 flex justify-between bg-black/60 p-1 opacity-100 transition-opacity sm:opacity-0 sm:group-hover:opacity-100">
        <button type="button" title="Définir comme principale" data-testid={`${testid}-set-main`} onClick={onMain} className={isMain ? "text-[#FF6A00]" : "text-zinc-300 hover:text-[#FF6A00]"}><Star className="h-3.5 w-3.5" fill={isMain ? "currentColor" : "none"} /></button>
        <button type="button" title="Retirer" data-testid={`${testid}-remove`} onClick={onRemove} className="text-zinc-300 hover:text-red-400"><X className="h-3.5 w-3.5" /></button>
      </div>
    </div>
  );
}

export function GalleryField({ images = [], main = "", onChange, testid }) {
  const [busy, setBusy] = useState(false);
  const upload = async (files) => {
    const list = [...files].slice(0, GALLERY_MAX - images.length);
    if (!list.length) { toast.error(`${GALLERY_MAX} photos maximum`); return; }
    setBusy(true);
    const refs = [];
    for (const f of list) {
      try { refs.push((await uploadFile(f, "gallery")).ref); } catch (e) { toast.error(`${f.name} : ${errMsg(e)}`); }
    }
    setBusy(false);
    onChange((p) => { const next = [...p.images, ...refs].slice(0, GALLERY_MAX); return { images: next, image: p.image || next[0] || "" }; });
  };
  const remove = (r) => onChange((p) => { const next = p.images.filter((x) => x !== r); return { images: next, image: p.image === r ? (next[0] || "") : p.image }; });
  return (
    <div data-testid={testid}>
      <div className="mt-1 flex flex-wrap gap-2">
        {images.map((r, i) => <GalleryThumb key={r} value={r} isMain={r === main} testid={`${testid}-photo-${i}`} onMain={() => onChange((p) => ({ ...p, image: r }))} onRemove={() => remove(r)} />)}
        {images.length < GALLERY_MAX && (
          <label className="flex h-20 w-20 cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-zinc-700 text-[11px] text-zinc-500 transition-colors hover:border-[#FF6A00]/60 hover:text-zinc-300">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-5 w-5" />}
            {busy ? "Envoi…" : "Ajouter"}
            <input type="file" multiple accept="image/png,image/jpeg,image/webp" className="hidden" data-testid={`${testid}-input`} disabled={busy} onChange={(e) => { upload(e.target.files || []); e.target.value = ""; }} />
          </label>
        )}
      </div>
      <p className="mt-1.5 text-[11px] text-zinc-600">{images.length}/{GALLERY_MAX} photos · l'étoile choisit la photo affichée sur la carte du catalogue</p>
    </div>
  );
}

function Preview({ value, meta, onRemove, testid, large }) {
  const { url, type } = useStoredUrl(value);
  const isPdf = type === "application/pdf" || meta?.content_type === "application/pdf";
  return (
    <div className="mt-2 flex items-center gap-2" data-testid={`${testid}-preview`}>
      {isPdf ? (
        <a href={url || undefined} target="_blank" rel="noreferrer" className="flex h-14 items-center gap-2 rounded-lg border border-zinc-800 bg-zinc-900 px-3 text-xs text-zinc-300 hover:border-[#FF6A00]/50">
          <FileText className="h-5 w-5 text-[#FF6A00]" />PDF{meta?.pages ? ` · ${meta.pages} page${meta.pages > 1 ? "s" : ""}` : ""}
        </a>
      ) : url ? <img src={url} alt="" className={large ? "max-h-40 rounded-lg border border-zinc-800" : "h-14 w-14 rounded-lg border border-zinc-800 object-cover"} /> : <span className="h-14 w-14 animate-pulse rounded-lg bg-zinc-800" />}
      <button type="button" data-testid={`${testid}-remove`} onClick={onRemove} className="text-xs text-red-400">Retirer</button>
    </div>
  );
}

export default function FileField({ value, onChange, accept, kind, testid, large = false }) {
  const [busy, setBusy] = useState(false);
  const [meta, setMeta] = useState(null);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const data = await uploadFile(file, kind);
      setMeta(data);
      onChange(data.ref);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  return (
    <div>
      <label data-testid={`${testid}-button`} className={`mt-1 inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-zinc-700 bg-zinc-900 px-3.5 py-1.5 text-xs font-medium text-zinc-200 transition-colors hover:border-[#FF6A00]/60 hover:text-white ${busy ? "pointer-events-none opacity-60" : ""}`}>
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5 text-[#FF6A00]" />}
        {busy ? "Envoi…" : value ? "Remplacer le fichier" : "Choisir un fichier"}
        <input type="file" accept={accept} data-testid={testid} disabled={busy} className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
      </label>
      {!busy && value && <Preview value={value} meta={meta} testid={testid} large={large} onRemove={() => { setMeta(null); onChange(""); }} />}
    </div>
  );
}
